import React from 'react';
import { Manufacturer, Size, Role, ProductionStatus, ShipClass } from '../../services/shipService';
import styles from './ShipFilters.module.css';

interface ShipFiltersProps {
  manufacturers: Manufacturer[];
  sizes: Size[];
  roles: Role[];
  shipClasses: ShipClass[];
  productionStatuses: ProductionStatus[];
  selectedManufacturer: string;
  selectedSize: string;
  selectedRole: string;
  selectedClass: string;
  selectedStatus: string;
  searchQuery: string;
  onManufacturerChange: (value: string) => void;
  onSizeChange: (value: string) => void;
  onRoleChange: (value: string) => void;
  onClassChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onClearFilters: () => void;
}

const ShipFilters: React.FC<ShipFiltersProps> = ({
  manufacturers,
  sizes,
  roles,
  shipClasses,
  productionStatuses,
  selectedManufacturer,
  selectedSize,
  selectedRole,
  selectedClass,
  selectedStatus,
  searchQuery,
  onManufacturerChange,
  onSizeChange,
  onRoleChange,
  onClassChange,
  onStatusChange,
  onSearchChange,
  onClearFilters
}) => {
  const hasActiveFilters = selectedManufacturer || selectedSize || selectedRole || selectedClass || selectedStatus || searchQuery;

  return (
    <div className={styles.filters}>
      <div className={styles.searchSection}>
        <input
          type="text"
          placeholder="🔍 Search ships by name, manufacturer, or role..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.filterGrid}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Manufacturer</label>
          <select
            value={selectedManufacturer}
            onChange={(e) => onManufacturerChange(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Manufacturers</option>
            {manufacturers.map((m, index) => (
              <option key={`${m.code}-${index}`} value={m.name}>
                {m.name} ({m.shipCount})
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Size</label>
          <select
            value={selectedSize}
            onChange={(e) => onSizeChange(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Sizes</option>
            {sizes.map((s) => (
              <option key={s.size} value={s.size}>
                {s.size} ({s.shipCount})
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Role</label>
          <select
            value={selectedRole}
            onChange={(e) => onRoleChange(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Roles</option>
            {roles.map((r) => (
              <option key={r.role} value={r.role}>
                {r.role} ({r.shipCount})
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Hull Class</label>
          <select
            value={selectedClass}
            onChange={(e) => onClassChange(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Classes</option>
            {shipClasses.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Production Status</label>
          <select
            value={selectedStatus}
            onChange={(e) => onStatusChange(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Statuses</option>
            {productionStatuses.map((s) => (
              <option key={s.status} value={s.status}>
                {s.status} ({s.shipCount})
              </option>
            ))}
          </select>
        </div>
      </div>

      {hasActiveFilters && (
        <div className={styles.clearFilters}>
          <button onClick={onClearFilters} className={styles.clearButton}>
            ✕ Clear All Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default ShipFilters;
